import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '@/lib/api-client';

export type BarcodeSettings = {
  /** Super-admin gate — our team enables the feature per tenant. */
  superAdminEnabled: boolean;
  /** Gate on AND a starting barcode saved: generation actually works. */
  active: boolean;
  barcodeStart: string | null;
  barcodeStep: number;
  nextBarcode: string | null;
  /** The next few codes, so the config card can show what the series looks like. */
  preview: string[];
  catalog: {
    /** Most common barcode length among this tenant's products (null if none). */
    commonLength: number | null;
    commonCount: number;
    totalWithBarcode: number;
  };
};

/**
 * The tenant's auto-barcode numbering, owned by the products service. Read by
 * the POS Config → Barcodes card and by the product form's Generate button.
 */
export function useBarcodeSettings(enabled = true) {
  return useQuery<{ data: BarcodeSettings }>({
    queryKey: ['barcode-settings'],
    queryFn: () => productsApi.get('/tenant-settings/barcode'),
    staleTime: 60_000,
    enabled,
  });
}

export function useUpdateBarcodeSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { barcodeStart: string | null; barcodeStep: number }) =>
      productsApi.put<{ data: Partial<BarcodeSettings> }>('/tenant-settings/barcode', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['barcode-settings'] }),
  });
}

/**
 * Allocate the next barcode in the tenant's series. Deliberately NOT a query —
 * every call consumes a number, so it must only ever fire on an explicit click.
 */
export function useGenerateBarcode() {
  return useMutation({
    mutationFn: () => productsApi.post<{ data: { barcode: string; source: string } }>('/products/barcode/generate', {}),
  });
}
